import { getDescricao, isCiap } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { AcompanhamentoCondicaoSaudeTematicaEnum, FaixaEtariaEnum } from 'graphql/types.generated'
import { grupoCondicao as grupoCondicaoRecord, identidadeGeneroRecord, sexoRecord } from 'types/enums'
import { isUndefinedOrNull } from 'util/checks'
import { dateAsDdMmYyyy } from 'util/date/formatDate'
import { AcompanhamentoFilterConfig, faixaEtariaRecord } from 'view/acompanhamento/model/model-acompanhamentoFilter'
import { IVCF_RESULTADOS, IVCF_SUBGRUPOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'
import { faixaEtariaFormatter } from 'view/busca-ativa/vacinacao/utils-buscaAtivaVacinacao'

import {
  AcompanhamentoCondicaoSaudeFormModel,
  condicaoMaternalFiltroRecord,
  periodoRegistroTriaRecord,
  ResultadosTriaEnum,
} from '../../model/model-acompanhamentoCondicaoSaude'
import { hasGrupoCondicaoOrCidCiap, isProblemaAtivoOuNaoIncluido } from '../../utils-acompanhamentoCondicaoSaude'

export const listaTematicaLabelRecord: Record<AcompanhamentoCondicaoSaudeTematicaEnum, string> = {
  [AcompanhamentoCondicaoSaudeTematicaEnum.DESENVOLVIMENTO_INFANTIL]: 'Des. infantil',
  [AcompanhamentoCondicaoSaudeTematicaEnum.DIABETES]: 'Diabetes',
  [AcompanhamentoCondicaoSaudeTematicaEnum.GERAL]: 'Geral',
  [AcompanhamentoCondicaoSaudeTematicaEnum.GESTANTE_PUERPERA]: 'Gestação e puerpério',
  [AcompanhamentoCondicaoSaudeTematicaEnum.HIPERTENSAO_ARTERIAL]: 'Hipertensão arterial',
  [AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA]: 'Pessoa idosa',
  [AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_MULHER]: 'Saúde da mulher',
  [AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_BUCAL]: 'Saúde bucal',
}

export const filterConfig: Readonly<AcompanhamentoFilterConfig<AcompanhamentoCondicaoSaudeFormModel>[]> = [
  {
    label: 'Lista temática:',
    getValue: (f) => listaTematicaLabelRecord[f.tematica],
  },
  {
    label: 'Grupo de condições prioritários:',
    getValue: ({ filtrosAvancados }) =>
      filtrosAvancados?.grupoCondicao?.map((g) => grupoCondicaoRecord[g.grupo]).join(', '),
  },
  {
    label: 'CIAP2 e CID10:',
    getValue: ({ filtrosAvancados }) =>
      filtrosAvancados?.problemasCondicoes
        ?.map((problema) => `${isCiap(problema) ? 'CIAP2' : 'CID10'}: ${getDescricao(problema)} - ${problema.codigo}`)
        .join(' | '),
  },
  {
    label: 'Buscar problemas/condições:',
    getValue: ({ filtrosAvancados }) =>
      `${filtrosAvancados?.ativoListaProblema ? 'Ativos na lista de problemas e condições' : ''}${
        filtrosAvancados?.ativoListaProblema && filtrosAvancados?.naoIncluidoListaProblema ? ', ' : ''
      }${filtrosAvancados?.naoIncluidoListaProblema ? 'Não incluídos na lista de problemas e condições' : ''}`,
    shouldRender: ({ filtrosAvancados }) =>
      hasGrupoCondicaoOrCidCiap(filtrosAvancados) && isProblemaAtivoOuNaoIncluido(filtrosAvancados),
  },
  {
    label: 'Microárea:',
    getValue: (f) => f.microareas?.join(', '),
  },
  {
    label: 'Sexo:',
    getValue: ({ filtrosAvancados }) => filtrosAvancados?.sexo?.map((sexo) => sexoRecord[sexo]).join(', '),
  },
  {
    label: 'Identidade de gênero:',
    getValue: ({ filtrosAvancados }) =>
      filtrosAvancados?.identidadeGenero?.map((genero) => identidadeGeneroRecord[genero]).join(', '),
  },
  {
    label: 'Período do último atendimento:',
    shouldRender: ({ filtrosAvancados }) =>
      !!filtrosAvancados?.periodoUltimoAtendimento?.startDate && !!filtrosAvancados?.periodoUltimoAtendimento?.endDate,
    getValue: ({ filtrosAvancados }) =>
      `${dateAsDdMmYyyy(filtrosAvancados?.periodoUltimoAtendimento.startDate)} até ${dateAsDdMmYyyy(
        filtrosAvancados?.periodoUltimoAtendimento.endDate
      )}`,
  },
  {
    label: 'Período do registro:',
    getValue: (f) => periodoRegistroTriaRecord[f?.tria?.periodoRegistro],
  },
  {
    label: 'Em risco de insegurança alimentar:',
    getValue: () => 'Sim',
    shouldRender: (f) => f.tria?.resultadosTria === ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR,
  },
  {
    label: 'Sem registro da TRIA no período:',
    getValue: () => 'Sim',
    shouldRender: (f) => f.tria?.resultadosTria === ResultadosTriaEnum.SEM_REGISTRO_TRIA_NO_PERIODO,
  },
  {
    label: 'Raça/Cor:',
    getValue: ({ filtrosAvancados }) => filtrosAvancados?.racaCor?.nome.titleCase(),
  },
  {
    label: 'Faixa etária:',
    getValue: ({ filtrosAvancados }) => {
      const tipo = filtrosAvancados?.tipoFaixaEtaria
      const tipoLabel = faixaEtariaRecord[filtrosAvancados?.tipoFaixaEtaria]
      const rangeLabel =
        tipo === FaixaEtariaEnum.OUTRA ? `(${faixaEtariaFormatter(filtrosAvancados?.faixaEtariaRange)})` : ''
      return `${tipoLabel} ${rangeLabel}`
    },
    shouldRender: ({ filtrosAvancados }) => filtrosAvancados?.tipoFaixaEtaria !== FaixaEtariaEnum.TODAS_FAIXAS,
  },
  {
    label: 'Índice:',
    getValue: ({ filtrosAvancados }) =>
      filtrosAvancados?.ivcf?.riscoVulnerabilidade.map((risco) => IVCF_RESULTADOS[risco].shortTitle).join(', '),
    shouldRender: ({ filtrosAvancados }) => !!filtrosAvancados?.ivcf?.riscoVulnerabilidade?.length,
  },
  {
    label: 'Pontuação:',
    getValue: ({ filtrosAvancados }) => {
      const p = filtrosAvancados?.ivcf?.pontuacao
      if (!isUndefinedOrNull(p.firstValue) && !isUndefinedOrNull(p.secondValue)) {
        return `${p.firstValue} até ${'ponto'.pluralizeAndConcatValue(p.secondValue)}`
      }
      if (!isUndefinedOrNull(p.firstValue)) {
        return `Acima de ${'ponto'.pluralizeAndConcatValue(p.firstValue)}`
      }
      if (!isUndefinedOrNull(p.secondValue)) {
        return `Abaixo de ${'ponto'.pluralizeAndConcatValue(p.secondValue)}`
      }
      return
    },
    shouldRender: ({ filtrosAvancados }) => !!filtrosAvancados?.ivcf?.pontuacao,
  },
  {
    label: 'Apenas cidadãos idosos sem registro do IVCF-20:',
    getValue: () => 'Sim',
    shouldRender: ({ filtrosAvancados }) => !!filtrosAvancados?.ivcf?.exibirApenasCidadaosIdososSemIvcf,
  },
  {
    label: 'Dimensões alteradas:',
    getValue: ({ filtrosAvancados }) =>
      filtrosAvancados?.ivcf?.subgrupo.map((subgrupo) => IVCF_SUBGRUPOS[subgrupo].label).join(', '),
    shouldRender: ({ filtrosAvancados }) => !!filtrosAvancados?.ivcf?.subgrupo?.length,
  },
  {
    label: 'Condição maternal:',
    getValue: ({ filtrosAvancados }) => condicaoMaternalFiltroRecord[filtrosAvancados?.condicaoMaternal],
    shouldRender: ({ filtrosAvancados }) => !!filtrosAvancados?.condicaoMaternal,
  },
] as const
